import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { AddressQueueSummary, GeocodeMatchLevel, PendingAddress } from '../types';
import AddressFixer from './AddressFixer';
import LocationQueue from './LocationQueue';

/** Ime operatera se pamti da se ne kuca pri svakoj potvrdi. */

/**
 * Red za potvrdu adresa kupaca.
 *
 * Merilo ovog ekrana je broj poteza po adresi. Operater ga otvara desetine puta
 * nedeljno, pa je sve podređeno tome da potvrda bude pogled i klik: pin je već
 * postavljen tamo gde geokoder misli da jeste, razlog čekanja stoji iznad mape,
 * a posle potvrde se sledeća adresa otvara sama.
 *
 * Sama ispravka je u <c>AddressFixer</c>, jer isti posao radi i poslovođa.
 */
export default function AddressQueue() {
  const [queue, setQueue] = useState<PendingAddress[]>([]);
  const [summary, setSummary] = useState<AddressQueueSummary | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runMessage, setRunMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [pending, counts] = await Promise.all([api.addressQueue(), api.addressSummary()]);
      setQueue(pending);
      setSummary(counts);
      setActiveId((current) =>
        current !== null && pending.some((a) => a.id === current) ? current : pending[0]?.id ?? null
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const active = useMemo(() => queue.find((a) => a.id === activeId) ?? null, [queue, activeId]);

  const runGeocoder = async (retry = false) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.runGeocoding(retry ? 200 : 50, retry);
      setRunMessage(result.explanation);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** Posle potvrde se otvara sledeća adresa, da se ne bira ručno svaki put. */
  const advance = (finishedId: number) => {
    const index = queue.findIndex((a) => a.id === finishedId);
    const next = queue[index + 1] ?? queue[index - 1] ?? null;
    setQueue((current) => current.filter((a) => a.id !== finishedId));
    setActiveId(next?.id ?? null);
    setSummary((current) =>
      current ? { ...current, pending: Math.max(0, current.pending - 1) } : current
    );
  };


  return (
    <div className="app__body">
      {/* Objekti se potvrđuju retko, ali zaustave turu kad nisu potvrđeni.
          Stoje iznad reda adresa jer je to posao od jednom, a adrese su
          svakodnevne. */}
      <LocationQueue />

      <section className="panel">
        <h2>
          Čeka potvrdu{' '}
          {summary && <span className="muted">· {summary.pending} adresa</span>}
        </h2>

        <div className="queue__actions">
          <button type="button" onClick={() => void runGeocoder()} disabled={busy}>
            {busy ? 'Radim…' : 'Pusti geokoder'}
          </button>
          {summary && summary.notGeocoded > 0 && (
            <span className="muted">{summary.notGeocoded} još nije obrađeno</span>
          )}
          {summary?.notGeocoded === 0 && queue.length > 0 && (
            <button type="button" className="link" onClick={() => void runGeocoder(true)} disabled={busy}>
              Probaj ceo red ponovo
            </button>
          )}
        </div>

        {runMessage && <p className="explanation">{runMessage}</p>}

        {queue.length === 0 ? (
          <p className="empty">Nema adresa koje čekaju potvrdu.</p>
        ) : (
          <ul className="queue">
            {queue.map((address) => (
              <li key={address.id}>
                <button
                  type="button"
                  className={`queue__item${address.id === activeId ? ' queue__item--active' : ''}`}
                  onClick={() => setActiveId(address.id)}
                >
                  <span className="queue__address">
                    {address.rawAddress || '(bez ulice)'}, {address.rawCity || '(bez mesta)'}
                  </span>
                  <span className={`queue__level queue__level--${levelClass(address.matchLevel)}`}>
                    {levelLabel(address.matchLevel)}
                  </span>
                  <span className="muted queue__note">{address.note}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <h2>Potvrda</h2>

        {error && <div className="health health--bad">{error}</div>}

        {!active ? (
          <p className="muted">Izaberi adresu iz reda.</p>
        ) : (
          <>
            <p className="queue__headline">
              {active.street || '(bez ulice)'} {active.houseNumber ?? ''}
              <br />
              <span className="muted">
                {active.resolvedCity ?? active.rawCity}
                {active.postalCode ? ` · ${active.postalCode}` : ''} · {active.erpDocumentKey}
              </span>
            </p>

            <AddressFixer
              address={active}
              onDone={advance}
            />
          </>
        )}
      </section>
    </div>
  );
}

function levelLabel(level: GeocodeMatchLevel): string {
  switch (level) {
    case 'HouseNumber':
      return 'kućni broj';
    case 'Street':
      return 'ulica';
    case 'Place':
      return 'samo mesto';
    default:
      return 'ništa';
  }
}

function levelClass(level: GeocodeMatchLevel): string {
  switch (level) {
    case 'HouseNumber':
      return 'ok';
    case 'Street':
      return 'warn';
    default:
      return 'bad';
  }
}
